import React from 'react'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { ProfileFormValues } from '../config'
import emptyAvatar from 'assets/images/emptyAvatar.webp'

import {
  FormikField,
  BaseDatePicker,
  HelperBlock,
  EditIcon,
  MobileMask,
} from 'shared'
import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'
import { useModal } from 'utils/providers/ModalProvider'
import { useAuth } from 'utils/providers/AuthProvider'
import ProfileFormAddress from './ProfileFormAddress'

const useStyles = makeStyles((theme) => ({
  profileImageContainer: {
    width: '80px',
    height: '80px',
    overflow: 'hidden',
    borderRadius: '100%',

    '& img': { width: '100%', height: '100%', objectFit: 'cover' },
    border: '2px solid #EFECF1',

    [theme.breakpoints.down('sm')]: {
      width: '100px',
      height: '100px',
    },
  },
  editProfileImageIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '6px',
    backgroundColor: '#E4CAFB',
    transform: 'translate(25%, 25%)',
    cursor: 'pointer',
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    margin: '24px  0',

    [theme.breakpoints.down('sm')]: {
      margin: '32px 0 20px 0',
    },
  },

  nameAndPhotoContainer: {
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
}))

const ProfileFormGeneral = () => {
  const classes = useStyles()
  const { user } = useAuth()
  const { showModal } = useModal()
  const { setFieldValue, values, errors } = useFormikContext<
    ProfileFormValues & { address: { state: string } }
  >()

  const profilePhotoUrl = user?.profile.profileImageUrl || emptyAvatar

  return (
    <ProfileFormWrapper title="General information">
      <Grid mt={2} container spacing={2}>
        <Grid
          pl={2}
          container
          gap={1.5}
          alignItems="center"
          className={classes.nameAndPhotoContainer}
        >
          <Grid position="relative" mr={1.5}>
            <Grid className={classes.profileImageContainer}>
              <img src={profilePhotoUrl} />
            </Grid>
            <Grid
              className={classes.editProfileImageIcon}
              onClick={() =>
                showModal('uploadAvatarModal', { showToast: false })
              }
            >
              <EditIcon />
            </Grid>
          </Grid>

          <Grid width="100%">
            <FormikField
              name="firstName"
              label="First name"
              placeholder="Enter first name"
              error={errors?.firstName || ''}
              onChange={(e: { target: { value: string } }) =>
                setFieldValue('firstName', e.target.value)
              }
            />
          </Grid>
          <Grid width="100%">
            <FormikField
              name="lastName"
              label="Last name"
              placeholder="Enter last name"
              onChange={(e: { target: { value: string } }) =>
                setFieldValue('lastName', e.target.value)
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormikField
            disabled
            name="email"
            label="Email"
            placeholder="Enter email"
          />
        </Grid>
        <Grid item xs={12}>
          <FormikField
            name="phoneNumber"
            label="Phone number"
            placeholder="Enter phone number"
            type="tel"
            InputProps={{ components: { Input: MobileMask } }}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <BaseDatePicker
            value={values.birthDate ? moment(values.birthDate) : null}
            label="Date of birth"
            minDate={moment().utc().subtract(100, 'years')}
            maxDate={moment().utc().subtract(18, 'years')}
            onChange={(val) => setFieldValue('birthDate', moment(val).toDate())}
            format={'MM-DD-YYYY'}
            error={errors?.birthDate || ''}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <HelperBlock label="You must be at least 18 years old to participate." />
        </Grid>
      </Grid>
      <Typography component="h3" className={classes.title}>
        Shipping address
      </Typography>
      <ProfileFormAddress />
    </ProfileFormWrapper>
  )
}

export default ProfileFormGeneral
